import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment-timezone";
import {
  useListAchReviewInstances,
  useApproveAchReviewStageInstance,
  type DocumentReviewInstanceSummary,
  type DocumentReviewStageInstanceSummary,
  type DocumentReviewStageDecisionStatus,
} from "@/lib/api/ach";
import { CommonDestinationTimezone } from "@/lib/utils";
import {
  getAchInstanceStatusBadge,
  getAchStageStatusBadge,
  getAchCurrentStage,
} from "@/section/ach/components/reviewInstanceUtils";
import { Loader2, RefreshCcw } from "lucide-react";
import { TaskInstanceActionComponentProps } from "./types";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SendAchForm } from "./SendAchForm";

export function AchSendFormTaskAction({
  taskInstance,
  templateName,
  taskTitle,
  currentUserId,
}: TaskInstanceActionComponentProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const approveStage = useApproveAchReviewStageInstance();
  const [pendingAction, setPendingAction] = useState<{
    stageId: string;
    status: DocumentReviewStageDecisionStatus;
  } | null>(null);

  const listResult = useListAchReviewInstances(
    { task_instance_id: taskInstance.id },
    { enabled: !!taskInstance.id }
  );

  const reviewPayload = listResult.data;
  const isLoading = listResult.isLoading || listResult.isFetching;
  const hasDataError = !!reviewPayload && !reviewPayload.success;
  const reviewInstances: DocumentReviewInstanceSummary[] = hasDataError
    ? []
    : reviewPayload?.data?.instances || [];
  const queryErrorMessage = hasDataError
    ? reviewPayload?.message || "Unable to load ACH review sessions for this task."
    : null;

  const totalInstances = reviewInstances.length;

  const formatDate = (date?: string | Date | null) => {
    if (!date) return "—";
    return moment(date).tz(CommonDestinationTimezone).format("MMM DD, YYYY h:mm A");
  };

  const handleStageDecision = async (
    stageId: string,
    status: DocumentReviewStageDecisionStatus
  ) => {
    setPendingAction({ stageId, status });
    try {
      const result = await approveStage({
        stage_instance_id: stageId,
        status,
      });

      if (result.success) {
        toast({
          title: status === "approved" ? "Stage approved" : "Stage declined",
          description: "The review stage was updated successfully.",
        });
        await Promise.all([
          listResult.refetch(),
          queryClient.invalidateQueries({ queryKey: ["ach", "review-instances"] }),
        ]);
      } else {
        toast({
          title: "Unable to update stage",
          description: result.message || "Please try again in a moment.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Unable to update stage",
        description: (error as Error)?.message || "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setPendingAction(null);
    }
  };

  const renderInstance = (instance: DocumentReviewInstanceSummary) => {
    const sortedStages = [...instance.StageInstances].sort(
      (a, b) => a.ReviewStage.stage_order - b.ReviewStage.stage_order
    );

    const currentStage = getAchCurrentStage(instance.StageInstances);
    const canAction =
      currentStage &&
      currentUserId &&
      currentStage.assigned_user_id === currentUserId &&
      (currentStage.status === "pending" || currentStage.status === "in_progress");
    const isActionPending = pendingAction?.stageId === currentStage?.id;

    return (
      <div key={instance.id} className="space-y-4 rounded-xl border border-border p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              ACH Review Session
            </p>
            <div className="text-lg font-semibold">
              {instance.Workflow.review_type || "ACH Review"}
            </div>
            <p className="text-sm text-muted-foreground">
              Created {formatDate(instance.created_at)}
            </p>
            {instance.AchDocumentInstance && (
              <p className="text-sm text-muted-foreground">
                Customer:{" "}
                <span className="font-medium text-foreground">
                  {instance.AchDocumentInstance.customer_name || "Unknown"}
                </span>
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {getAchInstanceStatusBadge(instance.status)}
            {currentStage ? (
              <Badge variant="outline" className="text-xs">
                Current Stage: {currentStage.ReviewStage.stage_order}
                {currentStage.ReviewStage.name && ` • ${currentStage.ReviewStage.name}`}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-xs bg-green-50 text-green-700 border-green-200"
              >
                All stages complete
              </Badge>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {sortedStages.map((stage) => (
            <StageRow
              key={stage.id}
              stage={stage}
              currentStageId={currentStage?.id}
              formatDate={formatDate}
            />
          ))}
        </div>

        <div className="flex flex-col gap-3 border-t border-dashed pt-3 text-sm text-muted-foreground md:flex-row md:items-center md:justify-between">
          {currentStage ? (
            <span>
              {canAction
                ? "You are assigned to the current stage."
                : `Waiting for ${
                    currentStage.AssignedUser?.name || "an assignee"
                  } to complete Stage ${currentStage.ReviewStage.stage_order}.`}
            </span>
          ) : (
            <span>All stages have been completed for this review session.</span>
          )}

          {canAction && currentStage && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={isActionPending}
                onClick={() => handleStageDecision(currentStage.id, "denied")}
                className="gap-1"
              >
                {isActionPending && pendingAction?.status === "denied" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="font-medium">Decline</span>
                )}
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={isActionPending}
                onClick={() => handleStageDecision(currentStage.id, "approved")}
                className="gap-1"
              >
                {isActionPending && pendingAction?.status === "approved" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <span className="font-medium">Approve</span>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-lg border border-border p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {templateName || ""}
          </p>
          <h3 className="text-xl font-semibold">
            {taskTitle || "Send out ACH Form"}
            {templateName && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({templateName})
              </span>
            )}
          </h3>
          <p className="text-sm text-muted-foreground">
            Track ACH review sessions and manage stage approvals, or send new ACH forms.
          </p>
        </div>
      </div>

      <Tabs defaultValue="review-sessions" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="review-sessions">Review Sessions</TabsTrigger>
          <TabsTrigger value="send-form">Send ACH Form</TabsTrigger>
        </TabsList>

        <TabsContent value="review-sessions" className="space-y-6 mt-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-muted-foreground">
              {isLoading
                ? "Checking for existing ACH review sessions..."
                : `${totalInstances} review session${totalInstances === 1 ? "" : "s"} found for this task.`}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={isLoading}
                onClick={() => listResult.refetch()}
              >
                <RefreshCcw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                Refresh status
              </Button>
            </div>
          </div>

          {queryErrorMessage && (
            <Alert variant="destructive">
              <AlertTitle>Unable to load review sessions</AlertTitle>
              <AlertDescription>{queryErrorMessage}</AlertDescription>
            </Alert>
          )}

          {isLoading && !reviewPayload ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : totalInstances === 0 ? (
            <div className="rounded-lg border border-dashed p-6 text-center text-muted-foreground">
              No ACH review sessions have been created for this task yet.
            </div>
          ) : (
            <div className="space-y-4">
              {reviewInstances.map((instance) => renderInstance(instance))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="send-form" className="mt-6">
          <SendAchForm 
            taskInstance={taskInstance}
            onSuccess={() => {
              listResult.refetch();
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

interface StageRowProps {
  stage: DocumentReviewStageInstanceSummary;
  currentStageId?: string;
  formatDate: (date?: string | Date | null) => string;
}

function StageRow({ stage, currentStageId, formatDate }: StageRowProps) {
  const isCurrent = stage.id === currentStageId;
  const isCompleted =
    stage.status === "approved" || stage.status === "denied" || stage.status === "skipped";

  return (
    <div
      className={`rounded-lg border p-3 ${
        isCurrent ? "border-blue-400 bg-blue-50/30" : ""
      } ${isCompleted ? "opacity-90" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="text-xs">
          Stage {stage.ReviewStage.stage_order}
        </Badge>
        {stage.ReviewStage.name && (
          <span className="text-sm font-medium text-foreground">{stage.ReviewStage.name}</span>
        )}
        {getAchStageStatusBadge(stage.status)}
        {isCurrent && (
          <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">
            Current
          </Badge>
        )}
      </div>
      <div className="mt-2 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
        <div>
          Assigned to:{" "}
          <span className="font-medium text-foreground">
            {stage.AssignedUser?.name || "Unassigned"}
          </span>
        </div>
        <div>
          Started:{" "}
          <span className="font-medium text-foreground">{formatDate(stage.started_at)}</span>
        </div>
        <div>
          Completed:{" "}
          <span className="font-medium text-foreground">{formatDate(stage.completed_at)}</span>
        </div>
        {stage.ReviewedBy && (
          <div>
            Reviewed by:{" "}
            <span className="font-medium text-foreground">{stage.ReviewedBy.name}</span>
          </div>
        )}
        {stage.reason && (
          <div className="sm:col-span-2">
            Reason: <span className="font-medium text-foreground">{stage.reason}</span>
          </div>
        )}
        {stage.notes && (
          <div className="sm:col-span-2">
            Notes: <span className="font-medium text-foreground">{stage.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
}

